// The monthly Artemis allowance (artemisapi docs/client-guide-api-3797.md). GET v3/Usage's
// AllowanceUsd is null when the user is unlimited (limits off, as in Production, or exempt);
// then nothing is shown. Shown as a percentage, never dollars.

export const WARN_AT = 80

// "Oct 31 at 7 PM": the reset (00:00 UTC on the 1st) in the user's local time, with the hour, since
// west of UTC it falls the evening before. timeZone is for tests.
export const resetLabel = (iso, locale, timeZone) => {
  const t = Date.parse(iso || '')
  if (!Number.isFinite(t)) return null
  const zone = timeZone ? { timeZone } : {}
  const day = new Date(t).toLocaleDateString(locale || [], { month: 'short', day: 'numeric', ...zone })
  const hour = new Date(t).toLocaleTimeString(locale || [], { hour: 'numeric', ...zone })
  return `${day} at ${hour}`
}

import { tokenRates, tokensLabel } from './tokens'
const dateLabel = (iso, locale) => {
  const t = Date.parse(iso || '')
  return Number.isFinite(t) ? new Date(t).toLocaleDateString(locale || [], { month: 'short', day: 'numeric', year: 'numeric' }) : null
}

// For the meter and the notices: { metered, percent, warn, limit, onCredits, credits, creditsExpire, resets };
// metered false when unlimited. The allowance is spent first, then purchased credits (API-3800):
// limit only when the server says the next question is refused (LimitReached), so a user whose
// allowance is used but who has credit left is onCredits, not at the limit.
export const usageState = usage => {
  if (!usage || usage.AllowanceUsd == null) return { metered: false, percent: null, warn: false, limit: false, onCredits: false, credits: 0, creditsExpire: null, resets: null }
  const raw = Number(usage.PercentUsed)
  const percent = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 0
  const credits = Math.max(0, Number(usage.CreditsUsd) || 0)
  const limit = usage.LimitReached === true
  const onCredits = !limit && percent >= 100 && credits > 0
  return {
    metered: true,
    percent,
    warn: !limit && !onCredits && percent >= WARN_AT,
    limit,
    onCredits,
    credits,
    // As tokens, never dollars (which would show the margin over the pack's price).
    creditsLeft: credits > 0 ? tokensLabel(credits, tokenRates(usage)) : null,
    creditsExpire: credits > 0 ? dateLabel(usage.NextCreditExpiresAt) : null,
    resets: resetLabel(usage.ResetsAt)
  }
}

// The notice over the composer, or null: { kind: 'warn' | 'credits' | 'limit', text, buyUrl, dismissible }.
// warn and credits can be dismissed; limit can't, since it says why Artemis won't answer. In a group,
// partners can still be messaged without @Artemis. buyUrl offers buying credits on every notice (credits can
// be bought at any time); null where credits aren't sold.
export const usageNotice = (state, { group = false, buyUrl = null } = {}) => {
  if (!state || !state.metered) return null
  const resets = state.resets ? ` It resets ${state.resets}.` : ''
  if (state.limit) {
    return { kind: 'limit', dismissible: false, buyUrl: buyUrl || null, text: `You've used this month's Artemis.${resets}${group ? ' You can still message the group without @Artemis.' : ''}` }
  }
  if (state.onCredits) return { kind: 'credits', dismissible: true, buyUrl: buyUrl || null, text: `This month's allowance is used. Artemis is using your credits: ${state.creditsLeft} left.` }
  if (state.warn) {
    const then = state.credits > 0 ? ' After that, Artemis uses your credits.' : ''
    return { kind: 'warn', dismissible: true, buyUrl: buyUrl || null, text: `You've used ${state.percent}% of this month's Artemis.${resets}${then}` }
  }
  return null
}

// A dismissed notice stays hidden for that kind until the allowance resets: { kind }:{ ResetsAt }. So moving
// from warn to credits, or a new month, shows the notice again.
export const noticeKey = (notice, usage) => (notice ? `${notice.kind}:${(usage && usage.ResetsAt) || ''}` : null)

const DISMISSED = 'artemis.usageNoticeDismissed'

export const dismissedNotice = () => {
  try { return window.localStorage.getItem(DISMISSED) } catch (e) { return null }
}

export const dismissNotice = key => {
  try { window.localStorage.setItem(DISMISSED, key) } catch (e) { /* storage full or off: hidden until reload */ }
}
